/**
 * `.motion` — the poser's own parameters, as an authored file.
 *
 * ==== WHY THIS IS A FILE AND NOT A `const` ==============================
 *
 * `docs/25-pixel-tools.md` §4's rule is that **a format is earned by a
 * mechanical writer**. §3.1 refuses one for the weapon lobe on exactly that
 * ground — *"nothing writes it but a person"* — and §4 grants one to `.pxm` and
 * §2 to `.plan` because `pixel mask set` and `pixel plan set` write them.
 *
 * The poser's constants had no writer, so they were `const`s in
 * `pixelBody.ts` and that was the right answer. `pixel motion edit` is a
 * mechanical writer, so the rule now points the other way, and the alternative
 * it points away from is the one §2 already rejected for plans: *"a tool that
 * edits seven numbers by rewriting TypeScript cannot promise the rewrite was
 * lossless."* The same sentence holds for thirty-five numbers, with two extra
 * teeth — a `const` initialised as `4 / 45.5` has no source form an editor can
 * put back, and the eighty lines of measurement above `HEAD_BOB_DROP` are
 * exactly what a source rewriter would put at risk.
 *
 * So: a text format with a byte-identity contract, written through the three
 * gates in `tools/lib/author.mjs`, like every other authored asset here.
 *
 * ==== WHAT IT CARRIES, AND WHAT IT DELIBERATELY DOES NOT ================
 *
 * **Amplitudes, rates, offsets and thresholds — never shape.** Whether the head
 * bob is `sin^2` and not a centred cosine, whether the legs are driven by
 * `|sin φ|` about the vertical and not by a signed swing about the hip, whether
 * a crouch is a fold and not a scale: those are the decisions #86, #104 and #98
 * *are*, each carries pages of argument, and each is a change with a case behind
 * it rather than a number to try. They stay in code with their argument, the way
 * a bone's parent and role stay in the `.plan` text while its seven numbers move.
 *
 * **A value the poser reads is a parameter; a value only a test reads is
 * evidence.** `REFERENCE_STANCE` is 17.5 px of ankle separation on a 45.5 px
 * figure in `sprites/castlevania.png`. Nothing poses with it —
 * `tests/gait.test.ts` holds `KNEE_EXTEND` *against* it — so putting it in this
 * file would hand an author a slider that moves the goalpost instead of the
 * body. That is `tools/lib/measure.mjs`'s rule about the height verdict, one
 * document over: the measurement is reported and never reconciled.
 *
 * ==== A NUMBER, AND WHY THERE IS A SECOND SPELLING OF ONE ===============
 *
 * The plain spelling is `planText.ts`'s and is a deliberate restatement rather
 * than a copy: `String(Number(tok)) === tok`, the language's own
 * shortest-round-tripping form, which is the only rule under which a file
 * survives being rewritten by a program. `tests/pixelBody.test.ts` spends that
 * agreement rather than asserting it here — every token this format accepts is
 * one `parseBodyPlan` accepts, driven through the shipping parser, so the two
 * formats cannot drift into two ideas of what a number is.
 *
 * The **ratio** — `4/45.5` — is this format's own, and it exists because of what
 * these numbers are. `HEAD_BOB_DROP` is *four pixels of collar travel on a
 * forty-five-and-a-half pixel figure*, read off a sheet; written as
 * `0.08791208791208791` it is seventeen digits nobody measured, and the
 * measurement is gone from the document that holds it. So a value may be a ratio
 * of two authored numbers, it is written back exactly as it was authored, and
 * **the denominator is the author's and not the slider's**: dragging moves the
 * numerator and keeps the figure the measurement was taken on, exactly as
 * dragging a bone in `pixel plan edit` moves an angle's offset and keeps the
 * direction word its author chose. `4/45.5` becomes `4.25/45.5`; it never
 * becomes `0.0934065934065934`.
 *
 * A ratio's denominator must be positive and must not be `1`: a negative
 * denominator is a second spelling of a negative numerator and `4/1` is a second
 * spelling of `4`, and a format that accepts two spellings of one value cannot
 * promise a round trip.
 *
 * ==== WHY AN UNKNOWN PARAMETER IS NOT A PARSE ERROR =====================
 *
 * It is gate 3, and for `planText.ts`'s reason for carrying a tissue by *name*:
 * a file naming a knob the build has not got should fail "at resolution with the
 * document's name attached rather than posing a bone into slot `undefined`".
 * `parseMotion` therefore accepts any name, and `motionFromDoc` — handed the
 * poser's own vocabulary rather than a list kept here — refuses a name it does
 * not have and a name it has and the document omits. `pixel motion set --param
 * wobble=0.3` parses, round-trips byte for byte, and is refused with the file
 * untouched, which is `--bone blob.tissue=BOGUS` one format over.
 */

/**
 * A value as the file spells it.
 *
 * `den` is `null` for a plain decimal. It is not folded into `num` at parse
 * time, because the whole reason the spelling exists is that the two numbers are
 * the measurement and the quotient is not.
 */
export interface MotionValue {
  readonly num: number;
  /** The figure the numerator was measured against, or `null` for a plain decimal. */
  readonly den: number | null;
}

/** One `param` line, with any `#` block the author wrote above it. */
export interface MotionParamDoc {
  /** The `#` lines above this parameter, verbatim, or empty. */
  readonly comment: readonly string[];
  readonly key: string;
  readonly value: MotionValue;
}

/** A parsed `.motion`. */
export interface MotionDoc {
  /** Leading `#` comment lines, verbatim and including the `#`. */
  readonly header: readonly string[];
  readonly id: string;
  readonly params: readonly MotionParamDoc[];
}

class MotionError extends Error {}

function fail(where: string, line: number, msg: string): never {
  throw new MotionError(`${where}:${line}: ${msg}`);
}

/**
 * A number in the one spelling this format allows.
 *
 * `String(Number(tok)) === tok`, which rejects `5.0`, `+5`, `05`, `5.`, `.5`,
 * `1e1` and `-0` at no cost in precision, and `Infinity` — which stringifies to
 * itself — by the finiteness test beside it. See the header for why this is
 * stated again here rather than imported: `planText.ts`'s `num` is private to
 * that parser, and the agreement between the two is asserted rather than
 * assumed.
 */
function num(where: string, line: number, field: string, tok: string): number {
  const n = Number(tok);
  if (!Number.isFinite(n) || String(n) !== tok) {
    fail(where, line, `${field}: ${JSON.stringify(tok)} is not a number in canonical form`);
  }
  return n;
}

/**
 * `0.07`, or `4/45.5`.
 *
 * Exported because `pixel motion set --param headBobDrop=4/45.5` has to read the
 * same grammar the file does, and a second reader of a value is how a format
 * grows a second spelling — which is `parsePlanAngle`'s docblock, and it is the
 * same risk here for the same reason.
 */
export function parseMotionValue(tok: string, where = '<motion>', line = 0): MotionValue {
  const slash = tok.indexOf('/');
  if (slash < 0) return { num: num(where, line, 'value', tok), den: null };
  const n = num(where, line, 'value', tok.slice(0, slash));
  const d = num(where, line, 'value', tok.slice(slash + 1));
  if (d <= 0) {
    fail(where, line, `value: a ratio's denominator is the figure it was measured on and is positive, got ${tok}`);
  }
  if (d === 1) fail(where, line, `value: \`${tok}\` is a second spelling of \`${tok.slice(0, slash)}\``);
  return { num: n, den: d };
}

/** The number a value means. */
export function motionValue(v: MotionValue): number {
  return v.den === null ? v.num : v.num / v.den;
}

/** Write a value back out, in the one spelling. */
export function motionValueText(v: MotionValue): string {
  return v.den === null ? String(v.num) : `${String(v.num)}/${String(v.den)}`;
}

/**
 * Parse a `.motion` document.
 *
 * Strict on structure for `parseBodyPlan`'s reason: a parser that accepts two
 * spellings of one document cannot promise a round trip, and the round trip is
 * gate 2 of the only thing allowed to replace this file. The only tolerances are
 * the ones a text editor imposes without asking — CRLF, and a missing final
 * newline.
 *
 * A blank line belongs to a comment block and nowhere else, which is what makes
 * "one blank line before a parameter that is argued for, none before one that is
 * not" a property of the bytes rather than a habit.
 */
export function parseMotion(text: string, where = '<motion>'): MotionDoc {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  let i = 0;
  const header: string[] = [];
  while (i < lines.length && lines[i].startsWith('#')) header.push(lines[i++]);

  if (i >= lines.length) fail(where, i + 1, 'no `motion` line');
  const head = lines[i].split(' ');
  if (head[0] !== 'motion' || head.length !== 2) {
    fail(where, i + 1, `expected \`motion <id>\`, got ${JSON.stringify(lines[i])}`);
  }
  const id = head[1];
  if (!/^[a-z][a-z0-9-]*$/.test(id)) fail(where, i + 1, `motion: ${JSON.stringify(id)} is not an id`);
  i++;

  const params: MotionParamDoc[] = [];
  const seen = new Set<string>();
  while (i < lines.length) {
    const blank = lines[i] === '';
    if (blank) i++;
    const comment: string[] = [];
    while (i < lines.length && lines[i].startsWith('#')) comment.push(lines[i++]);
    if (blank !== comment.length > 0) {
      fail(where, i + 1, 'a blank line belongs to a `#` block above a parameter and nowhere else');
    }
    if (i >= lines.length) fail(where, i, 'a `#` block with no parameter under it');

    const parts = lines[i].split(' ');
    if (parts[0] !== 'param' || parts.length !== 3) {
      fail(where, i + 1, `expected \`param <name> <value>\`, got ${JSON.stringify(lines[i])}`);
    }
    const key = parts[1];
    // A name, not a path: the poser resolves it, and a name that could be a
    // number or carry punctuation is a name a message cannot be read back off.
    if (!/^[a-z][A-Za-z0-9]*$/.test(key)) fail(where, i + 1, `param: ${JSON.stringify(key)} is not a parameter name`);
    if (seen.has(key)) fail(where, i + 1, `param: \`${key}\` is set twice — a parameter is named once`);
    seen.add(key);
    params.push({ comment, key, value: parseMotionValue(parts[2], where, i + 1) });
    i++;
  }

  if (params.length === 0) fail(where, i, 'no parameters — an empty motion document poses nothing');
  return { header, id, params };
}

/** Write a document back out. The inverse of `parseMotion`, exactly. */
export function serializeMotion(doc: MotionDoc): string {
  const out: string[] = [...doc.header];
  out.push(`motion ${doc.id}`);
  for (const p of doc.params) {
    if (p.comment.length > 0) out.push('');
    for (const line of p.comment) out.push(line);
    out.push(`param ${p.key} ${motionValueText(p.value)}`);
  }
  return out.join('\n') + '\n';
}

/**
 * Resolve a document against the poser's vocabulary — **gate 3**.
 *
 * `keys` is passed in rather than imported, for the reason `planFromDoc` takes
 * its tissue resolver: this file then holds no copy of what the poser has, and a
 * test can hand it a vocabulary that does not exist, which is the assertion that
 * kills a hardcoded table here.
 *
 * Both directions are refusals and both matter. A parameter the poser does not
 * have is a knob wired to nothing — the author would turn it and watch the sheet
 * not move. A parameter the poser has and the document omits is worse: the value
 * would have to come from somewhere, and the only somewhere left is a default in
 * the code, which is the second statement of an authored number this file exists
 * to remove.
 *
 * A `Map`, not an object index — `docs/25` §5.2's resolver trap, which is live
 * here in the way it is nowhere else in this file: `params` names come out of a
 * text file, so `param toString 1` is a line somebody can write.
 */
export function motionFromDoc(doc: MotionDoc, keys: readonly string[]): ReadonlyMap<string, number> {
  const want = new Set(keys);
  const out = new Map<string, number>();
  for (const p of doc.params) {
    if (!want.has(p.key)) {
      throw new MotionError(
        `motion ${doc.id}: unknown parameter ${p.key} — the poser has ${keys.join(', ')}`,
      );
    }
    out.set(p.key, motionValue(p.value));
  }
  const missing = keys.filter((k) => !out.has(k));
  if (missing.length > 0) {
    throw new MotionError(
      `motion ${doc.id}: no value for ${missing.join(', ')} — ` +
        'every parameter the poser reads is authored here, so there is no default to fall back to',
    );
  }
  return out;
}

/**
 * One resolved value, or a throw naming it.
 *
 * The one reader of a resolved table, so that "a key the poser reads is a key
 * the document must carry" is enforced in one place rather than at thirty-five
 * call sites — and so that a table built by hand in a test is held to the same
 * standard as one that came off a file. `own` is not reachable from here (a
 * `Map` has no prototype chain to walk into) and the `Map` is the reason why —
 * `planText.own` is the spelling a plain table would have needed and this file
 * has no plain table to need it.
 */
export function motionAt(values: ReadonlyMap<string, number>, key: string): number {
  const v = values.get(key);
  if (v === undefined) throw new MotionError(`motion: no value for ${key}`);
  return v;
}
